import * as React from 'react';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Mousewheel, Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Breakpoint, Color, IconName } from '../../enums';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import {
  Container,
  Dot,
  DotsContainer,
  FullSizeButton,
  FullSizeButtonsContainer,
  FullSizeImage,
  FullSizePopupContainer,
  ImageElement,
  SwiperContainer,
  SwiperWrapper
} from './photosSlider.styled';
import { PhotosSliderProps } from './photosSlider.types';

export const PhotosSlider: React.FC<PhotosSliderProps> = (props: PhotosSliderProps): JSX.Element => {
  const { images, imageAlt = '', className }: PhotosSliderProps = props;
  const [swiperInstance, setSwiperInstance]: UseState<SwiperType | null> = useState<SwiperType | null>(null);
  const [fullSizeSwiperInstance, setFullSizeSwiperInstance]: UseState<SwiperType | null> = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex]: UseState<number> = useState<number>(0);
  const [isActiveFullSize, setIsActiveFullSize]: UseState<boolean> = useState<boolean>(false);
  const isMobile: boolean = useMediaQuery({ query: Breakpoint.Mobile });
  const isSmallMobile: boolean = useMediaQuery({ query: Breakpoint.SmallMobile });

  const onOpenOrCloseFullSize: () => void = (): void => {
    setIsActiveFullSize(!isActiveFullSize);
  };

  return (
    <Container className={className}>
      {isActiveFullSize ? (
        <FullSizePopupContainer>
          <Swiper
            onSwiper={setFullSizeSwiperInstance}
            onSlideChange={(swiper: SwiperType): void => setActiveIndex(swiper.realIndex)}
            initialSlide={activeIndex}
            slidesPerView={1}
            centeredSlides={true}
            speed={600}
            observer={true}
            mousewheel={isSmallMobile && {
              sensitivity: 0.5,
              releaseOnEdges: true,
            }}
            modules={[Mousewheel]}
            style={{ width: '100%', height: '100%' }}
          >
            {images.map((image: string, index: number): JSX.Element => (
              <SwiperSlide key={index} style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                <FullSizeImage
                  src={image}
                  alt={imageAlt}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {fullSizeSwiperInstance && (
            <FullSizeButtonsContainer>
              {!!images.length && !isSmallMobile && (
                <FullSizeButton onClick={(): void => fullSizeSwiperInstance.slidePrev()}>
                  <Icon name={IconName.ArrowLeft25} size={18} color={Color.Gray500} />
                </FullSizeButton>
              )}
              <FullSizeButton onClick={onOpenOrCloseFullSize}>
                <Icon name={IconName.Close} size={18} color={Color.Gray500} />
              </FullSizeButton>
              {!!images.length && !isSmallMobile && (
                <FullSizeButton onClick={(): void => fullSizeSwiperInstance.slideNext()}>
                  <Icon name={IconName.ArrowRight25} size={18} color={Color.Gray500} />
                </FullSizeButton>
              )}
            </FullSizeButtonsContainer>
          )}
        </FullSizePopupContainer>
      ) : (
        <SwiperWrapper>
          {swiperInstance && !!images.length && (
            <DotsContainer>
              {images.map((image: string, index: number): JSX.Element => (
                <Dot
                  $isActive={index === activeIndex}
                  key={image}
                  onClick={(): void => swiperInstance.slideTo(index)}
                />
              ))}
            </DotsContainer>
          )}
          <SwiperContainer>
            <Swiper
              onSwiper={setSwiperInstance}
              onSlideChange={(swiper: SwiperType): void => setActiveIndex(swiper.realIndex)}
              initialSlide={activeIndex}
              direction={isMobile ? 'horizontal' : 'vertical'}
              slidesPerView={isMobile ? 1 : 'auto'}
              freeMode={!isMobile}
              mousewheel={!isMobile && {
                sensitivity: 0.5,
                releaseOnEdges: true,
              }}
              spaceBetween={0}
              speed={600}
              modules={[Mousewheel]}
              style={{ width: '100%', height: '100%' }}
            >
              {images.map((image: string, index: number): JSX.Element => (
                <SwiperSlide key={index} onClick={onOpenOrCloseFullSize}>
                  <ImageElement
                    src={image}
                    alt={imageAlt}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperContainer>
        </SwiperWrapper>
      )}
    </Container>
  );
};
